import * as React from "react";

export interface UseDealSearchDebounceOptions {
  /** Minimum query length before search engages — below this, callers treat the
   * value as empty and fall back to the active tab filter. */
  minChars: number;
  /** Debounce window for typed input. Clears immediately when the value empties. */
  ms: number;
}

export interface UseDealSearchDebounceResult {
  /** Debounced, trimmed, lowercased search query — safe to feed directly to a
   * `.toLowerCase().includes(query)` row filter. */
  query: string;
  /** True when the debounced query is at least `minChars` long — i.e. search
   * should override the tab filter. */
  isSearching: boolean;
}

/** Shared debounce + min-chars trigger for the deal search inputs.
 *
 * Used by both the partner workspace and the manager Pipeline Ledger so they
 * stay in lockstep on debounce timing and the "≥N chars overrides tab" rule.
 * Empties immediately when the input clears so the grid doesn't flash stale
 * results when a tab/scorecard click resets the box.
 */
export function useDealSearchDebounce(
  value: string,
  { minChars, ms }: UseDealSearchDebounceOptions,
): UseDealSearchDebounceResult {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    if (value === "") {
      setDebounced("");
      return;
    }
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);

  const query = debounced.trim().toLowerCase();
  return { query, isSearching: query.length >= minChars };
}
